import axios from "axios";
import { always, cond, has, mergeLeft, pathOr, pipe } from "ramda";
import Cookies from "universal-cookie";
import { API_INTEGRATION_KEY, API_URL } from "../constants/common";
import { setApiError, setRemainingCredits } from "../reducers/app/actions";
import { redactParams } from "../helpers/app";

const REDACT_PARAMETERS = [
  "X-USER-TOKEN",
  "X-API-KEY",
  "x-api-key",
  "x-user-token",
];

const cookies = new Cookies();

let store;

export const injectStore = (_store) => {
  store = _store;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
});

const parseData = (data) => {
  try {
    return JSON.stringify(JSON.parse(data), null, 4);
  } catch (e) {
    return data;
  }
};

const axiosLogger = (response) => {
  console.groupCollapsed(
    response.config.method.toUpperCase() +
      " " +
      pathOr("", ["config", "url"], response)
  );
  console.groupCollapsed("HEADERS");
  console.dir(
    JSON.stringify(
      redactParams(
        pathOr({}, ["config", "headers"], response),
        REDACT_PARAMETERS
      ),
      null,
      4
    )
  );
  console.groupEnd();

  console.groupCollapsed("BODY");
  console.dir(parseData(pathOr("", ["config", "data"], response)));
  console.groupEnd();

  console.log(`RESPONSE HTTP CODE: ${pathOr("", ["status"], response)}`);

  console.groupCollapsed("RESPONSE BODY");
  console.dir(JSON.stringify(pathOr({}, ["data"], response), null, 4));
  console.groupEnd();

  console.groupEnd();
};

const fetchApiKey = async () => {
  const response = await axios.get(
    `${process.env.REACT_APP_PYTHON_API_URL}/user/fetch-api-key`,
    {
      headers: {
        Authorization: `Bearer ${cookies.get("token")}`,
      },
    }
  );

  return {
    apiKey: response.data.data,
  };
};

const attachIntegrationKeyRequestInterceptor = (config) => {
  const makeHeaders = () => {
    return {
      "X-INTEGRATION-KEY": API_INTEGRATION_KEY,
    };
  };

  const headers = makeHeaders();

  return {
    ...config,
    headers: {
      ...config.headers,
      ...headers,
    },
  };
};

const attachApiKeyRequestInterceptor = (() => {
  const apiKey = {
    value: null,
    promisify: null,
  };

  const makeAttach = async () => {
    if (!apiKey.value) {
      if (!apiKey.promisify) {
        const promise = new Promise((resolve, reject) => {
          fetchApiKey()
            .then(({ apiKey }) => resolve(apiKey))
            .catch(reject);
        });

        apiKey.promisify = promise
          .then((key) => {
            apiKey.value = key;
          })
          .finally(() => {
            apiKey.promisify = null;
          });
      }

      await apiKey.promisify;
    }
  };

  return async (config) => {
    const makeHeaders = (customConfig) => {
      const makeAuth = () => {
        return {
          "x-api-key": apiKey.value,
        };
      };

      return {
        ...makeAuth(),
      };
    };

    await makeAttach();

    const headers = makeHeaders();

    return {
      ...config,
      headers: {
        ...config.headers,
        ...headers,
      },
    };
  };
})();

axiosInstance.interceptors.request.use(
  pipe(attachIntegrationKeyRequestInterceptor, attachApiKeyRequestInterceptor),
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    // CONSOLE LOG
    axiosLogger(response);

    const remainingCredits = pathOr(
      null,
      ["data", "remainingCredits"],
      response
    );
    if (remainingCredits) {
      store.dispatch(setRemainingCredits(remainingCredits));
    }

    return response.data;
  },
  (error) => {
    // CONSOLE LOG
    console.log("ERROR");
    axiosLogger(error.response);

    const statusCode = pathOr("XXX", ["response", "status"], error);
    const errorData = pathOr("XXX", ["response", "data"], error);

    store.dispatch(setApiError(errorData));

    if (statusCode === 408 || error.code === "ECONNABORTED") {
      return Promise.reject(error);
    }

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const data = pipe(
      cond([
        [has("response"), pathOr({}, ["response", "data"])],
        [has("request"), pathOr({}, ["request"])],
        [always(true), always(error)],
      ]),
      mergeLeft({
        statusCode,
        fromAxiosInterceptors: true,
      })
    )(error);
    const customConfig = pathOr({}, ["config", "customConfig"], error);

    // if (statusCode === 401 && !customConfig.noRedirect401) {
    //   return setTimeout(() => {
    //     history.push(PAGES.UNAUTHENTICATED.path);
    //   });
    // }

    if (statusCode === 403) {
    }

    if (statusCode === 404 && !customConfig.noRedirect404) {
    }

    if (statusCode === 500) {
    }

    // CONSOLE LOG
    console.log("REQUEST ERROR: ", error);

    return Promise.reject(data);
  }
);

export const Request = (
  config = {
    noToken: false,
    noRedirect404: false,
  }
) => {
  const normalizeConfig = {
    ...config,
    noToken: config.noToken || false,
    noRedirect404: config.noRedirect404 || false,
  };

  return {
    get(url, params = {}, options = {}) {
      return axiosInstance.get(url, {
        ...options,
        // @ts-ignore
        customConfig: normalizeConfig,
        params,
      });
    },

    post(url, data, options = {}) {
      return axiosInstance.post(url, data, {
        ...options,
        // @ts-ignore
        customConfig: normalizeConfig,
      });
    },

    put(url, data, options = {}) {
      return axiosInstance.put(url, data, {
        ...options,
        // @ts-ignore
        customConfig: normalizeConfig,
      });
    },

    delete(url, params, options = {}) {
      // return axiosInstance.request({
      //   ...options,
      //   customConfig: normalizeConfig,
      //   method: 'delete',
      //   url,
      //   params,
      // });

      return axiosInstance.delete(url, {
        ...options,
        // @ts-ignore
        customConfig: normalizeConfig,
        params,
      });
    },

    custom(config) {
      return axiosInstance(config);
    },

    getInstance() {
      return axiosInstance;
    },
  };
};

export default Request;
